import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import {
  PanelState,
  PanelDirection,
  PanelDockPosition,
  PanelTransitionTiming,
  PanelRelationshipBehavior,
  PanelRelationship,
  PanelAnimation,
  PanelConfig,
  PanelData,
  SavedPanelLayout,
  PanelContextState
} from './PanelTypes';

// Define the actions for our reducer
type PanelAction = 
  | { type: 'REGISTER_PANEL', payload: PanelConfig }
  | { type: 'UNREGISTER_PANEL', payload: { id: string } }
  | { type: 'SET_PANEL_STATE', payload: { id: string, state: PanelState } }
  | { type: 'RESIZE_PANEL', payload: { id: string, width: number | string } }
  | { type: 'REORDER_PANELS', payload: { order: string[] } }
  | { type: 'SAVE_LAYOUT', payload: { name: string } }
  | { type: 'LOAD_LAYOUT', payload: { name: string } }
  | { type: 'HIDE_ALL_PANELS' }
  | { type: 'SHOW_ALL_PANELS' }
  | { type: 'START_TRANSITION', payload: { id: string } }
  | { type: 'END_TRANSITION', payload: { id: string } }
  | { type: 'DOCK_PANEL', payload: { id: string, position: PanelDockPosition } };

// Initial state
const initialState: PanelContextState = {
  panels: {},
  visibleOrder: [],
  savedLayouts: {},
  isGlobalHidden: false,
  activeTransitions: []
};

// For debouncing - kept outside of React state
let lastExecutionTime = 0;
const DEBOUNCE_THRESHOLD = 200; // ms

// Helper function to calculate panel position
function calculatePanelPosition(panel: PanelConfig, existingPanels: Record<string, PanelData>) {
  const position: { left: string | number; right: string | number } = { left: 'auto', right: 'auto' };
  
  if (panel.position === 'left') {
    position.left = 0;
  } else if (panel.position === 'right') {
    position.right = 0;
  } else if (panel.position === 'center') {
    position.left = '50%';
    // Will be transformed in CSS
  }
  
  // If this panel overlays another panel, we need to adjust position
  if (panel.overlayTarget && existingPanels[panel.overlayTarget]) {
    const targetPanel = existingPanels[panel.overlayTarget];
    if (targetPanel.position === 'left') {
      // Position to the right of the target panel
      if (typeof targetPanel.actualWidth === 'number') {
        position.left = `${targetPanel.actualWidth}px`;
      } else {
        position.left = targetPanel.actualWidth;
      }
    } else if (targetPanel.position === 'right') {
      // Position to the left of the target panel
      if (typeof targetPanel.actualWidth === 'number') {
        position.right = `${targetPanel.actualWidth}px`;
      } else {
        position.right = targetPanel.actualWidth;
      }
    }
  }
  
  return position;
}

// Create the reducer
function panelReducer(state: PanelContextState, action: PanelAction): PanelContextState {
  switch (action.type) {
    case 'REGISTER_PANEL': {
      const panel = action.payload;
      const actualPosition = calculatePanelPosition(panel, state.panels);
      
      return {
        ...state,
        panels: {
          ...state.panels,
          [panel.id]: {
            ...panel,
            currentState: panel.defaultState,
            actualWidth: panel.width,
            actualPosition,
            isAnimating: false
          }
        },
        visibleOrder: state.visibleOrder.includes(panel.id) 
          ? state.visibleOrder 
          : [...state.visibleOrder, panel.id].sort((a, b) => {
            return (state.panels[a]?.zIndex || 0) - (state.panels[b]?.zIndex || 0);
          })
      };
    }
    case 'UNREGISTER_PANEL': {
      const { id } = action.payload;
      const { [id]: removed, ...restPanels } = state.panels;
      return {
        ...state,
        panels: restPanels,
        visibleOrder: state.visibleOrder.filter(panelId => panelId !== id)
      };
    }
    case 'SET_PANEL_STATE': {
      const { id, state: newState } = action.payload;
      if (!state.panels[id]) return state;
      
      return {
        ...state,
        panels: {
          ...state.panels,
          [id]: {
            ...state.panels[id],
            previousState: state.panels[id].currentState,
            currentState: newState
          }
        }
      };
    }
    case 'RESIZE_PANEL': {
      const { id, width } = action.payload;
      if (!state.panels[id]) return state;
      
      return {
        ...state,
        panels: {
          ...state.panels,
          [id]: {
            ...state.panels[id],
            actualWidth: width
          }
        }
      };
    }
    case 'REORDER_PANELS': {
      return {
        ...state,
        visibleOrder: action.payload.order
      };
    }
    case 'SAVE_LAYOUT': {
      const { name } = action.payload;
      const layout: SavedPanelLayout = {
        name,
        timestamp: Date.now(),
        panels: Object.entries(state.panels).map(([id, panel]) => ({
          id,
          state: panel.currentState,
          width: panel.actualWidth
        }))
      };
      
      return {
        ...state,
        savedLayouts: {
          ...state.savedLayouts,
          [name]: layout
        }
      };
    }
    case 'LOAD_LAYOUT': {
      const { name } = action.payload;
      const layout = state.savedLayouts[name];
      
      if (!layout) return state;
      
      const updatedPanels = { ...state.panels };
      
      // Apply layout to existing panels
      layout.panels.forEach(item => {
        if (updatedPanels[item.id]) {
          updatedPanels[item.id] = {
            ...updatedPanels[item.id],
            currentState: item.state,
            actualWidth: item.width
          };
        }
      });
      
      return {
        ...state,
        panels: updatedPanels
      };
    }
    case 'HIDE_ALL_PANELS': {
      // Store current state as previous layout
      const previousLayout: SavedPanelLayout = {
        name: '__previous__',
        timestamp: Date.now(),
        panels: Object.entries(state.panels).map(([id, panel]) => ({
          id,
          state: panel.currentState,
          width: panel.actualWidth
        }))
      };
      
      // Update all panels to hidden
      const updatedPanels = Object.entries(state.panels).reduce((acc, [id, panel]) => {
        acc[id] = {
          ...panel,
          previousState: panel.currentState,
          currentState: 'hidden'
        };
        return acc;
      }, {} as Record<string, PanelData>);
      
      return {
        ...state,
        panels: updatedPanels,
        previousLayout,
        isGlobalHidden: true
      };
    }
    case 'SHOW_ALL_PANELS': {
      if (!state.previousLayout) return state;
      
      const updatedPanels = { ...state.panels };
      
      // Restore previous states
      state.previousLayout.panels.forEach(item => {
        if (updatedPanels[item.id]) {
          updatedPanels[item.id] = {
            ...updatedPanels[item.id],
            currentState: item.state || updatedPanels[item.id].previousState || 'visible',
            actualWidth: item.width
          };
        }
      });
      
      return {
        ...state,
        panels: updatedPanels,
        isGlobalHidden: false
      };
    }
    case 'START_TRANSITION': {
      const { id } = action.payload;
      return {
        ...state,
        activeTransitions: [...state.activeTransitions, id],
        panels: {
          ...state.panels,
          [id]: {
            ...state.panels[id],
            isAnimating: true
          }
        }
      };
    }
    case 'END_TRANSITION': {
      const { id } = action.payload;
      return {
        ...state,
        activeTransitions: state.activeTransitions.filter(panelId => panelId !== id),
        panels: {
          ...state.panels,
          [id]: {
            ...state.panels[id],
            isAnimating: false
          }
        }
      };
    }
    case 'DOCK_PANEL': {
      const { id, position } = action.payload;
      if (!state.panels[id]) return state;
      
      return {
        ...state,
        panels: {
          ...state.panels,
          [id]: {
            ...state.panels[id],
            position
          }
        }
      };
    }
    default:
      return state;
  }
}

// Create the context type
export interface PanelContextType {
  state: PanelContextState;
  registerPanel: (config: PanelConfig) => void;
  unregisterPanel: (id: string) => void;
  setPanelState: (id: string, state: PanelState, options?: { skipRelationships?: boolean }) => void;
  resizePanel: (id: string, width: number | string) => void;
  reorderPanels: (order: string[]) => void;
  saveLayout: (name: string) => void;
  loadLayout: (name: string) => void;
  hideAllPanels: () => void;
  showAllPanels: () => void;
  startTransition: (id: string) => void;
  endTransition: (id: string) => void;
  getPanelRelationships: (id: string) => PanelRelationship[];
  executeChoreography: (targetId: string, targetState: PanelState) => void;
  dockPanel: (id: string, position: PanelDockPosition) => void;
  directSetPanelState: (id: string, state: PanelState) => void;
}

// Create the context with undefined default
const PanelContext = createContext<PanelContextType | undefined>(undefined);

// Create the provider component
export function PanelProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(panelReducer, initialState);
  
  // Store a ref to prevent circular updates
  const stateRef = useRef(state);
  
  // Update the ref whenever state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  // Method to handle panel relationships and choreography
  const executeChoreography = useCallback((targetId: string, targetState: PanelState) => {
    // Add debounce protection
    const now = Date.now();
    if (now - lastExecutionTime < DEBOUNCE_THRESHOLD) {
      console.log(`Debouncing choreography for ${targetId}, too soon after last execution`);
      return;
    }
    lastExecutionTime = now;
  
    // Access state from ref to avoid dependency issues
    const panels = stateRef.current.panels;
    
    // Process panel relationships
    const targetPanel = panels[targetId];
    if (!targetPanel || !targetPanel.relationships?.length) {
      // No relationships, just set state directly
      dispatch({ type: 'SET_PANEL_STATE', payload: { id: targetId, state: targetState } });
      return;
    }
    
    // Process relationships
    for (const relationship of targetPanel.relationships) {
      const relatedPanel = panels[relationship.with];
      if (!relatedPanel) continue;
      
      switch (relationship.behavior) {
        case 'collapse-when-opens':
          if (targetState === 'visible') {
            // Process by timing
            if (relationship.timing === 'before') {
              // Set related panel first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'collapsed' } 
              });
              
              // Then wait and set target panel
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: targetId, state: targetState } 
                });
              }, relationship.delay || 0);
            } else if (relationship.timing === 'after') {
              // Set target panel first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
              
              // Then wait and set related panel
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: relationship.with, state: 'collapsed' } 
                });
              }, relationship.delay || 0);
            } else {
              // Simultaneous by default
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'collapsed' } 
              });
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
            }
          }
          break;
          
        case 'open-when-collapses':
          if (targetState === 'collapsed') {
            if (relationship.timing === 'before') {
              // Related panel opens first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'visible' } 
              });
              
              // Then target panel collapses
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: targetId, state: targetState } 
                });
              }, relationship.delay || 0);
            } else if (relationship.timing === 'after') {
              // Target panel collapses first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
              
              // Then related panel opens
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: relationship.with, state: 'visible' } 
                });
              }, relationship.delay || 0);
            } else {
              // Simultaneous
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'visible' } 
              });
            }
          }
          break;
          
        case 'hide-when-shows':
          if (targetState === 'visible') {
            if (relationship.timing === 'before') {
              // Hide related panel first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'hidden' } 
              });
              
              // Then show target panel
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: targetId, state: targetState } 
                });
              }, relationship.delay || 0);
            } else if (relationship.timing === 'after') {
              // Show target panel first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
              
              // Then hide related panel
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: relationship.with, state: 'hidden' } 
                });
              }, relationship.delay || 0);
            } else {
              // Simultaneous
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'hidden' } 
              });
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
            }
          }
          break;
          
        case 'show-when-hides':
          if (targetState === 'hidden') {
            if (relationship.timing === 'before') {
              // Show related panel first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'visible' } 
              });
              
              // Then hide target panel
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: targetId, state: targetState } 
                });
              }, relationship.delay || 0);
            } else if (relationship.timing === 'after') {
              // Hide target panel first
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
              
              // Then show related panel
              setTimeout(() => {
                dispatch({ 
                  type: 'SET_PANEL_STATE', 
                  payload: { id: relationship.with, state: 'visible' } 
                });
              }, relationship.delay || 0);
            } else {
              // Simultaneous
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: targetId, state: targetState } 
              });
              dispatch({ 
                type: 'SET_PANEL_STATE', 
                payload: { id: relationship.with, state: 'visible' } 
              });
            }
          }
          break;
      }
    }
  }, []); // Empty dependency array - we're using stateRef instead
  
  // Create the context value with the actions
  const contextValue = React.useMemo(() => ({
    state,
    registerPanel: (config: PanelConfig) => {
      dispatch({ type: 'REGISTER_PANEL', payload: config });
    },
    directSetPanelState: (id: string, state: PanelState) => {
      dispatch({ type: 'SET_PANEL_STATE', payload: { id, state } });
    },
    unregisterPanel: (id: string) => {
      dispatch({ type: 'UNREGISTER_PANEL', payload: { id } });
    },
    setPanelState: (id: string, state: PanelState, options = {}) => {
      if (options.skipRelationships) {
        dispatch({ type: 'SET_PANEL_STATE', payload: { id, state } });
      } else {
        executeChoreography(id, state);
      }
    },
    resizePanel: (id: string, width: number | string) => {
      dispatch({ type: 'RESIZE_PANEL', payload: { id, width } });
    },
    reorderPanels: (order: string[]) => {
      dispatch({ type: 'REORDER_PANELS', payload: { order } });
    },
    saveLayout: (name: string) => {
      dispatch({ type: 'SAVE_LAYOUT', payload: { name } });
    },
    loadLayout: (name: string) => {
      dispatch({ type: 'LOAD_LAYOUT', payload: { name } });
    },
    hideAllPanels: () => {
      dispatch({ type: 'HIDE_ALL_PANELS' });
    },
    showAllPanels: () => {
      dispatch({ type: 'SHOW_ALL_PANELS' });
    },
    startTransition: (id: string) => {
      dispatch({ type: 'START_TRANSITION', payload: { id } });
    },
    endTransition: (id: string) => {
      dispatch({ type: 'END_TRANSITION', payload: { id } });
    },
    getPanelRelationships: (id: string) => {
      return state.panels[id]?.relationships || [];
    },
    executeChoreography,
    dockPanel: (id: string, position: PanelDockPosition) => {
      dispatch({ type: 'DOCK_PANEL', payload: { id, position }});
    }
  }), [state, executeChoreography]);

  // Localstorage save effect - using a separate effect with its own dependencies
  const saveToLocalStorage = useCallback(() => {
    try {
      localStorage.setItem('panelSystemState', JSON.stringify({
        panels: Object.fromEntries(
          Object.entries(state.panels).map(([id, panel]) => [
            id, 
            { currentState: panel.currentState, actualWidth: panel.actualWidth }
          ])
        )
      }));
    } catch (e) {
      console.error('Failed to save panel state to localStorage:', e);
    }
  }, [state]);
  
  // Separate effect for localStorage to prevent circular updates
  useEffect(() => {
    const timer = setTimeout(saveToLocalStorage, 500);
    return () => clearTimeout(timer);
  }, [saveToLocalStorage]);
  
  return (
    <PanelContext.Provider value={contextValue}>
      {children}
    </PanelContext.Provider>
  );
}

// Hook to use the context
export function usePanelSystem() {
  const context = useContext(PanelContext);
  if (context === undefined) {
    throw new Error('usePanelSystem must be used within a PanelProvider');
  }
  return context;
}

// Hook to use a specific panel
export function usePanel(id: string) {
  const { 
    state, 
    setPanelState, 
    resizePanel, 
    startTransition, 
    endTransition,
    dockPanel
  } = usePanelSystem();
  
  const panel = state.panels[id];
  
  const handleTransitionEnd = useCallback(() => {
    if (panel?.isAnimating) {
      endTransition(id);
    }
  }, [id, panel?.isAnimating, endTransition]);
  
  // Add refs for transition management
  const transitionRef = React.useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const element = transitionRef.current;
    if (!element) return;
    
    element.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      element.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [handleTransitionEnd]);
  
  const panelActions = React.useMemo(() => ({
    panel,
    isVisible: panel?.currentState === 'visible',
    isCollapsed: panel?.currentState === 'collapsed',
    isHidden: panel?.currentState === 'hidden',
    isAnimating: panel?.isAnimating || false,
    isDockable: panel?.dockable !== false,
    preferredDockPositions: panel?.preferredDockPositions || ['left', 'right', 'top', 'bottom'],
    currentPosition: panel?.position,
    transitionRef,
    showPanel: (options?: { skipRelationships?: boolean }) => {
      if (!panel) return;
      startTransition(id);
      setPanelState(id, 'visible', options);
    },
    hidePanel: (options?: { skipRelationships?: boolean }) => {
      if (!panel) return;
      startTransition(id);
      setPanelState(id, 'hidden', options);
    },
    collapsePanel: (options?: { skipRelationships?: boolean }) => {
      if (!panel) return;
      startTransition(id);
      setPanelState(id, 'collapsed', options);
    },
    togglePanel: (options?: { skipRelationships?: boolean }) => {
      if (!panel) return;
      startTransition(id);
      setPanelState(
        id, 
        panel.currentState === 'visible' ? 'hidden' : 'visible',
        options
      );
    },
    resizePanel: (width: number | string) => {
      if (!panel) return;
      resizePanel(id, width);
    },
    dockTo: (position: PanelDockPosition) => {
      if (!panel) return;
      dockPanel(id, position);
    }
  }), [
    panel, id, startTransition, setPanelState, resizePanel, dockPanel
  ]);
  
  return panelActions;
}